import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Download, Link2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { createStoredCertificatePdfBlob } from "../components/Certificate";
import { linkValidacaoCertificado, obterCertificado } from "../services/certificadoService";
import { formatCertificateDate } from "../utils/certificado";

const CertificadoPage = () => {
  const { idCertificado } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [copied, setCopied] = useState(false);

  // Copia o link publico; se o navegador bloquear a area de transferencia, mostra o link para copia manual.
  const handleCopyLink = async () => {
    const link = linkValidacaoCertificado(certificate.codigo_validacao);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt("Copie o link do certificado:", link);
    }
  };

  useEffect(() => {
    let active = true;
    let objectUrl;
    setLoading(true);
    setError("");
    setCertificate(null);
    setPdfUrl("");

    const load = async () => {
      try {
        // Consulta o registro escolhido e recria o mesmo PDF, sem gerar nova emissao.
        const saved = await obterCertificado(idCertificado);
        if (!active) return;
        const blob = await createStoredCertificatePdfBlob(saved);
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setCertificate(saved);
        setPdfUrl(objectUrl);
      } catch (err) {
        if (active) setError(err.message || "Não foi possível abrir o certificado.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    // Libera a memoria do PDF ao trocar de certificado ou sair da pagina.
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [idCertificado, attempt]);

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF9F7] text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      <Header />
      <main className="mx-auto w-full max-w-screen-xl flex-1 px-4 pb-16 pt-32 sm:px-6 lg:px-8 lg:pt-28">
        <Link to="/certificados" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-[#B95758] hover:underline dark:text-rose-300">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Voltar para meus certificados
        </Link>
        {loading ? (
          <p role="status" className="rounded-xl bg-white p-8 text-center shadow-sm dark:bg-zinc-900">Carregando certificado...</p>
        ) : error ? (
          <div role="alert" className="rounded-xl border border-red-200 bg-white p-6 dark:border-red-900 dark:bg-zinc-900">
            <p className="text-red-700 dark:text-red-300">{error}</p>
            <button type="button" onClick={() => setAttempt((value) => value + 1)} className="mt-4 rounded-lg bg-[#B95758] px-4 py-2 font-semibold text-white hover:bg-[#9f3f40]">Tentar novamente</button>
          </div>
        ) : certificate && (
          <>
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="mb-1 text-sm font-semibold text-[#B95758] dark:text-rose-300">Certificado de conclusão</p>
                <h1 className="break-words text-2xl font-bold sm:text-3xl">{certificate.nome_curso}</h1>
                <p className="mt-2 break-words text-zinc-600 dark:text-zinc-400">{certificate.nome_completo}</p>
              </div>
              <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                {/* So aparece quando o banco ja possui o codigo publico de validacao. */}
                {certificate.codigo_validacao && (
                  <button type="button" onClick={handleCopyLink} className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#B95758] px-5 py-3 font-semibold text-[#B95758] hover:bg-[#B95758]/10 dark:border-rose-300 dark:text-rose-300">
                    {copied ? <Check className="h-5 w-5" aria-hidden="true" /> : <Link2 className="h-5 w-5" aria-hidden="true" />}
                    {copied ? "Link copiado!" : "Copiar link"}
                  </button>
                )}
                <a href={pdfUrl} download={"certificado-" + certificate.id_certificado + ".pdf"} className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#B95758] px-5 py-3 font-semibold text-white hover:bg-[#9f3f40]">
                  <Download className="h-5 w-5" aria-hidden="true" /> Baixar PDF
                </a>
              </div>
            </div>
            <section aria-label="Visualização do certificado" className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800">
              <iframe title={"Certificado de " + certificate.nome_curso} src={pdfUrl + "#toolbar=0&navpanes=0&view=FitH"} className="aspect-[842/595] min-h-[360px] w-full sm:min-h-[580px]" />
            </section>
            <dl className="mt-6 grid gap-5 rounded-xl border border-zinc-200 bg-white p-6 text-sm dark:border-zinc-800 dark:bg-zinc-900 sm:grid-cols-2 lg:grid-cols-4">
              <div><dt className="text-zinc-500 dark:text-zinc-400">Conclusão</dt><dd className="mt-1 font-semibold"><time dateTime={certificate.data_conclusao}>{formatCertificateDate(certificate.data_conclusao)}</time></dd></div>
              <div><dt className="text-zinc-500 dark:text-zinc-400">Empresa emissora</dt><dd className="mt-1 break-words font-semibold">{certificate.company_name}</dd></div>
              <div><dt className="text-zinc-500 dark:text-zinc-400">CNPJ</dt><dd className="mt-1 font-semibold">{certificate.cnpj}</dd></div>
              <div><dt className="text-zinc-500 dark:text-zinc-400">Número do certificado</dt><dd className="mt-1 font-semibold">{certificate.id_certificado}</dd></div>
            </dl>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default CertificadoPage;
